/**
 * Beu admin — AI overview for one salon: daily cost vs revenue, cost by
 * kind, revenue lines, and every session / call / send that made it up.
 */
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import AdminWrapper from '../AdminWrapper';
import { CustomTooltip } from '../../finance/components/Charts';
import { adminAiService } from '../../../_services/adminAi.service';
import { KIND_LABELS, PRODUCT_LABELS, fmtPence, fmtInt, fmtMs, fmtTime, recentMonths, chartSeries, sessionPath } from './aiFormat';
import { FlagPills, MarginCell, MonthPicker } from './AdminAiOverview';
import UnitEconomics from './UnitEconomics';
import '../Admin.css';
import '../financials/AdminFinancials.css';
import './AdminAi.css';

const REVENUE_SOURCES = {
  bailey_upgrade: 'Beu + Bailey upgrade (old, incl. receptionist)',
  bailey_plus: 'Bailey Plus',
  bailey_upgrade_se: 'Bailey Plus (self-employed)',
  receptionist_addon: 'AI receptionist',
  receptionist_overage: 'Receptionist pay as you go',
  receptionist_pack: 'Receptionist pack',
  reminder_overage: 'Reminder overage',
  reminder_invoice: 'Month-end SMS invoice',
  marketing_messages: 'Marketing texts/emails (less free allowance)',
  direct_messages: 'Direct client messages',
  numbers: 'Extra phone numbers'
};

class AdminAiSalon extends Component {
  constructor(props) {
    super(props);
    const params = new URLSearchParams(props.location?.search || '');
    this.state = { month: params.get('month') || recentMonths()[0], page: 1, kind: '', loading: true, error: null, data: null };
  }

  componentDidMount() { this.load(); }

  salonId() { return this.props.match?.params?.salonId; }

  load = async () => {
    this.setState({ loading: true, error: null });
    try {
      const { month, page, kind } = this.state;
      const data = await adminAiService.getSalon(this.salonId(), { month, page, kind, limit: 50 });
      this.setState({ data, loading: false });
    } catch (e) {
      this.setState({ error: e.message || String(e), loading: false });
    }
  };

  setMonth = (month) => {
    this.setState({ month, page: 1 }, this.load);
    this.props.history && this.props.history.replace(`/beu-admin-x7k9/ai/salon/${this.salonId()}?month=${month}`);
  };

  renderChart(d) {
    const rows = chartSeries(d.series);
    return (
      <div className="fin-section">
        <div className="fin-section-header">
          <h3 className="fin-section-title">Daily cost vs revenue</h3>
          <span className="fin-section-sub">Revenue is monthly, spread evenly over the days</span>
        </div>
        <div className="ai-ov-chart">
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#737373' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#737373' }} tickLine={false} axisLine={false} tickFormatter={(v) => `£${v}`} width={56} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Cost" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="Revenue" stroke="#007EA0" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  renderBreakdown(d) {
    return (
      <div className="ai-ov-two">
        <div className="fin-section">
          <div className="fin-section-header"><h3 className="fin-section-title">Revenue</h3></div>
          <table className="fin-table">
            <tbody>
              {(d.revenueLines || []).map((l, i) => (
                <tr key={i}><td>{REVENUE_SOURCES[l.source] || l.source}</td><td className="num">{fmtPence(l.pence)}</td></tr>
              ))}
              {!(d.revenueLines || []).length && <tr><td className="fin-dim" colSpan={2}>No AI or messaging revenue this month.</td></tr>}
              <tr className="total"><td>Total</td><td className="num">{fmtPence(d.totals.revenue)}</td></tr>
            </tbody>
          </table>
          <table className="fin-table ai-ov-mini">
            <tbody>
              {Object.entries(d.revenueByProduct || {}).filter(([, v]) => v).map(([k, v]) => (
                <tr key={k}><td className="fin-dim">{PRODUCT_LABELS[k] || k}</td><td className="num fin-dim">{fmtPence(v)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="fin-section">
          <div className="fin-section-header"><h3 className="fin-section-title">Cost by kind</h3></div>
          <table className="fin-table">
            <tbody>
              {(d.costByKind || []).sort((a, b) => b.costPence - a.costPence).map((k) => (
                <tr key={k.kind} className="ai-ov-clickable" onClick={() => this.setState({ kind: this.state.kind === k.kind ? '' : k.kind, page: 1 }, this.load)}>
                  <td>{KIND_LABELS[k.kind] || k.kind}{this.state.kind === k.kind ? ' (filtered)' : ''}</td>
                  <td className="num fin-dim">{fmtInt(k.events)}</td>
                  <td className="num">{fmtPence(k.costPence)}</td>
                </tr>
              ))}
              <tr className="total"><td>Total</td><td /><td className="num">{fmtPence(d.totals.cost)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  renderSessions(d) {
    const s = d.sessions;
    const pages = Math.max(1, Math.ceil(s.total / s.limit));
    return (
      <div className="fin-section">
        <div className="fin-section-header">
          <h3 className="fin-section-title">Sessions, calls and sends ({fmtInt(s.total)}){this.state.kind ? ` — ${KIND_LABELS[this.state.kind] || this.state.kind}` : ''}</h3>
          <button className="ai-ov-btn" onClick={() => adminAiService.exportSalonEventsCsv(this.salonId(), this.state.month).catch((e) => window.alert(e.message))}>Export events CSV</button>
        </div>
        <div className="ai-ov-scroll">
          <table className="fin-table ai-ov-table">
            <thead>
              <tr>
                <th>Started</th><th>What</th><th className="num">Duration</th><th className="num">Lines</th>
                <th className="num">Model calls</th><th className="num">Tokens in / out</th><th className="num">Units</th><th className="num">Cost</th><th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {s.rows.map((r) => (
                <tr key={r.key} className="ai-ov-clickable" onClick={() => this.props.history.push(sessionPath(r.drill))}>
                  <td>{fmtTime(r.startedAt)}</td>
                  <td>
                    <Link to={sessionPath(r.drill)} onClick={(e) => e.stopPropagation()}>{r.kinds.map((k) => KIND_LABELS[k] || k).join(' + ')}</Link>
                    {r.errors > 0 && <span className="ai-ov-flag bad">{r.errors} failed</span>}
                  </td>
                  <td className="num">{fmtMs(r.durationMs)}</td>
                  <td className="num">{fmtInt(r.lines)}</td>
                  <td className="num">{r.rounds ? fmtInt(r.rounds) : '—'}</td>
                  <td className="num">{r.tokens.input || r.tokens.output ? `${fmtInt(r.tokens.input)} / ${fmtInt(r.tokens.output)}` : '—'}</td>
                  <td className="num">{r.seconds ? `${fmtInt(r.seconds)}s` : r.segments ? `${fmtInt(r.segments)} seg` : '—'}</td>
                  <td className="num">{fmtPence(r.costPence)}</td>
                  <td><FlagPills flags={r.flags.filter((f) => f !== 'fx_unverified')} /></td>
                </tr>
              ))}
              {!s.rows.length && <tr><td colSpan={9} className="fin-dim">Nothing recorded this month.</td></tr>}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="ai-ov-pager">
            <button className="ai-ov-btn" disabled={s.page <= 1} onClick={() => this.setState({ page: s.page - 1 }, this.load)}>Previous</button>
            <span className="fin-dim">Page {s.page} of {pages}</span>
            <button className="ai-ov-btn" disabled={s.page >= pages} onClick={() => this.setState({ page: s.page + 1 }, this.load)}>Next</button>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { month, loading, error, data } = this.state;
    return (
      <AdminWrapper>
        <div className="admin-page-header ai-ov-header">
          <div>
            <Link to={`/beu-admin-x7k9/ai?month=${month}`} className="ai-ov-back">← AI overview</Link>
            <h1 className="admin-page-title">{data ? data.salon.salonName : 'Salon'}</h1>
            {data && (
              <p className="admin-page-subtitle">
                Revenue {fmtPence(data.totals.revenue)} · Cost {fmtPence(data.totals.cost)} · Margin <MarginCell margin={data.totals.margin} pct={data.totals.marginPct} />
              </p>
            )}
          </div>
          <div className="ai-ov-actions">
            <MonthPicker value={month} onChange={this.setMonth} />
            <Link className="ai-ov-btn" to={`/beu-admin-x7k9/businesses/${this.salonId()}`}>Business record</Link>
          </div>
        </div>
        {loading && <div className="fin-loading">Loading…</div>}
        {error && <div className="fin-error">{error}</div>}
        {!loading && !error && data && (
          <div>
            {this.renderChart(data)}
            {this.renderBreakdown(data)}
            <UnitEconomics data={data.unitEconomics} title="Unit economics (this salon)" />
            {this.renderSessions(data)}
          </div>
        )}
      </AdminWrapper>
    );
  }
}

export default AdminAiSalon;
